import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// Available themes
export const THEMES = [
  { id: 'sat-light', name: 'SAT Light', icon: '☀️', type: 'light' },
  { id: 'sat-dark', name: 'SAT Dark', icon: '🌙', type: 'dark' },
  { id: 'sat-gradient', name: 'Gradient', icon: '🌈', type: 'light' },
  { id: 'light', name: 'Light', icon: '💡', type: 'light' },
  { id: 'dark', name: 'Dark', icon: '🌑', type: 'dark' },
  { id: 'cupcake', name: 'Cupcake', icon: '🧁', type: 'light' },
  { id: 'cyberpunk', name: 'Cyberpunk', icon: '🤖', type: 'light' },
  { id: 'forest', name: 'Forest', icon: '🌲', type: 'dark' },
  { id: 'dracula', name: 'Dracula', icon: '🧛', type: 'dark' },
];

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sat-theme') || 'sat-light';
    }
    return 'sat-light';
  });

  // Determine if current theme is dark
  const isDark = THEMES.find(t => t.id === theme)?.type === 'dark';

  useEffect(() => {
    // Apply theme to document
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('sat-theme', theme);
    
    // Update color-scheme for native elements
    const colorScheme = isDark ? 'dark' : 'light';
    document.documentElement.style.colorScheme = colorScheme;
  }, [theme, isDark]);

  // Toggle between light and dark variants
  const toggleDarkMode = () => {
    if (theme === 'sat-light') {
      setTheme('sat-dark');
    } else if (theme === 'sat-dark') {
      setTheme('sat-light');
    } else if (theme === 'sat-gradient') {
      setTheme('sat-dark');
    } else {
      // For other themes, toggle to corresponding light/dark
      const currentTheme = THEMES.find(t => t.id === theme);
      if (currentTheme?.type === 'light') {
        setTheme('sat-dark');
      } else {
        setTheme('sat-light');
      }
    }
  };

  const value = {
    theme,
    setTheme,
    isDark,
    toggleDarkMode,
    themes: THEMES,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
